import React, { useEffect, useState } from 'react';
import Button from '../common/Button/Button';
import Icon from '../common/Icon/Icon';
import { AppRequiredFieldTag } from '../common/appUi';
import InstructionRow from './InstructionRow';
import { getPositionConfig } from './getPositionConfig';
import type {
  PhotoGuidelineImageData,
  PhotoGuidelineSlot,
  PositionGuideKey,
} from './photoGuidelines.types';
import { getPhotoGuidelineSectionId } from './photoGuidelines.types';
import styles from './PositionGuideSection.module.css';

interface PositionGuideSectionProps {
  title: string;
  imageSrc: string;
  imageAlt: string;
  positionKey: PositionGuideKey;
  imageClassName?: string;
  instructions: {
    head: string;
    torso: string;
    arms: string;
    feet: string;
  };
  photo: PhotoGuidelineImageData | null;
  slot: PhotoGuidelineSlot;
  showMissingHint?: boolean;
  onSelectPhoto: (slot: PhotoGuidelineSlot) => void;
  onRemovePhoto: (slot: PhotoGuidelineSlot) => void;
}

const DEFAULT_IMAGE_HEIGHT = 268;

const formatFileSize = (bytes: number): string => `${Math.round(bytes / 1024)} kb`;

const PositionGuideSection: React.FC<PositionGuideSectionProps> = ({
  title,
  imageSrc,
  imageAlt,
  positionKey,
  imageClassName = '',
  instructions,
  photo,
  slot,
  showMissingHint = false,
  onSelectPhoto,
  onRemovePhoto,
}) => {
  const [imageHeight, setImageHeight] = useState(DEFAULT_IMAGE_HEIGHT);
  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null);
  const positionConfig = getPositionConfig(imageHeight)[positionKey];

  useEffect(() => {
    if (!imageElement) {
      return undefined;
    }

    const updateHeight = () => {
      const nextHeight = imageElement.offsetHeight;
      if (nextHeight > 0) {
        setImageHeight(nextHeight);
      }
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(imageElement);

    return () => observer.disconnect();
  }, [imageElement]);

  return (
    <section
      id={getPhotoGuidelineSectionId(slot)}
      className={`${styles.section} ${showMissingHint ? styles.sectionMissing : ''}`.trim()}
    >
      <h3 className={styles.title}>{title}</h3>

      {showMissingHint && (
        <AppRequiredFieldTag message={`Falta enviar a foto de ${title}.`} />
      )}

      <div className={styles.positionSection}>
        <div className={styles.imageContainer}>
          <img
            ref={setImageElement}
            src={imageSrc}
            alt={imageAlt}
            className={`${styles.positionImage} ${imageClassName}`.trim()}
          />
        </div>

        <div className={styles.instructions}>
          <InstructionRow text={instructions.head} stylesConfig={positionConfig.head} />
          <InstructionRow text={instructions.torso} stylesConfig={positionConfig.torso} />
          <InstructionRow text={instructions.arms} stylesConfig={positionConfig.arms} />
          <InstructionRow text={instructions.feet} stylesConfig={positionConfig.feet} />
        </div>
      </div>

      <Button
        type="button"
        variant="primary"
        size="app"
        className={styles.uploadButton}
        onClick={() => onSelectPhoto(slot)}
      >
        {photo ? 'Substituir' : 'Fazer upload'}
      </Button>

      {photo && (
        <div className={styles.previewContainer}>
          <div className={styles.previewThumbnail}>
            <img src={photo.previewUrl} alt={photo.fileName} className={styles.thumbnailImage} />
          </div>
          <div className={styles.previewInfo}>
            <p className={styles.previewFileName}>{photo.fileName}</p>
            <p className={styles.previewFileDetails}>
              {formatFileSize(photo.fileSize)} · {photo.fileType}
            </p>
          </div>
          <button
            type="button"
            className={styles.removeButton}
            onClick={() => onRemovePhoto(slot)}
            aria-label="Remover foto"
          >
            <Icon name="cancel" size={16} />
          </button>
        </div>
      )}
    </section>
  );
};

export default PositionGuideSection;
